// ============================================================================
// IssueMap Component (View)
// ============================================================================

import { useRef, useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import { Map, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import type { IssueMapProps } from "./IssueMap.types";
import { useIssueMapViewModel } from "./IssueMap.vm";
import { MapBoundsFitter } from "./MapBoundsFitter";
import { DEFAULT_MAP_HEIGHT, LASSO_COLOR } from "../../shared/constants";
import "./IssueMap.css";

/**
 * Map component for displaying job boundaries and related geographic features.
 * Supports multiple visualization modes for different wizard steps.
 */
export function IssueMap(props: IssueMapProps) {
	const { visualizationMode, height = DEFAULT_MAP_HEIGHT, lassoMode, onLassoComplete } = props;

	const vm = useIssueMapViewModel(props);
	const map = useMap();
	const geometryLibrary = useMapsLibrary("geometry");

	// Refs for lasso drawing state (avoids React re-renders during drawing)
	const lassoPathRef = useRef<google.maps.LatLngLiteral[]>([]);
	const completeLassoRef = useRef<() => void>(() => {});

	// Complete the lasso selection - check which features are inside the drawn polygon
	const completeLasso = useCallback(() => {
		const currentPath = lassoPathRef.current;

		if (!geometryLibrary || currentPath.length < 3) {
			lassoPathRef.current = [];
			return;
		}

		// Check which features are entirely contained in the lasso (all vertices inside)
		const lassoPolygon = new google.maps.Polygon({ paths: currentPath });
		const selectedIds = new Set<string>();

		vm.featureMetadata.forEach((meta) => {
			const isFullyInside = meta.paths.every((point) => {
				return geometryLibrary.poly.containsLocation(new google.maps.LatLng(point.lat, point.lng), lassoPolygon);
			});
			if (isFullyInside) {
				selectedIds.add(meta.id);
			}
		});

		// Notify parent and clear path
		onLassoComplete?.(selectedIds);
		lassoPathRef.current = [];
	}, [vm.featureMetadata, geometryLibrary, onLassoComplete]);

	// Keep completeLassoRef always pointing to latest completeLasso
	completeLassoRef.current = completeLasso;

	// Point-and-click lasso: click to add vertices, click near first point to close
	useEffect(() => {
		if (!map || !lassoMode) return;

		map.setOptions({ draggable: false });

		// Create native polyline for lasso visualization
		const lassoPolyline = new google.maps.Polyline({
			map,
			strokeColor: LASSO_COLOR,
			strokeWeight: 2,
			strokeOpacity: 0.8,
		});

		// Create a marker for the first point (click target to close polygon)
		let firstPointMarker: google.maps.Marker | null = null;

		const handleClick = (e: google.maps.MapMouseEvent) => {
			if (!e.latLng) return;

			const clickedPoint = e.latLng.toJSON();

			// If we have 3+ points and click near the first point, close the polygon
			if (lassoPathRef.current.length >= 3) {
				const firstPoint = lassoPathRef.current[0];
				const distance = Math.sqrt(
					Math.pow(clickedPoint.lat - firstPoint.lat, 2) + Math.pow(clickedPoint.lng - firstPoint.lng, 2),
				);
				// Close if within ~0.0005 degrees (~50m at equator, scales with zoom)
				const zoom = map.getZoom() || 10;
				const threshold = 0.01 / Math.pow(2, zoom - 10); // Smaller threshold at higher zoom
				if (distance < threshold) {
					completeLassoRef.current();
					return;
				}
			}

			// Add the point
			lassoPathRef.current.push(clickedPoint);
			lassoPolyline.setPath(lassoPathRef.current);

			// Create/update first point marker
			if (lassoPathRef.current.length === 1) {
				firstPointMarker = new google.maps.Marker({
					position: clickedPoint,
					map,
					icon: {
						path: google.maps.SymbolPath.CIRCLE,
						scale: 8,
						fillColor: LASSO_COLOR,
						fillOpacity: 1,
						strokeColor: "#ffffff",
						strokeWeight: 2,
					},
					title: "Click to close polygon",
				});
				// Clicking the marker closes the polygon
				firstPointMarker.addListener("click", () => {
					if (lassoPathRef.current.length >= 3) {
						completeLassoRef.current();
					}
				});
			}
		};

		const clickListener = map.addListener("click", handleClick);

		return () => {
			google.maps.event.removeListener(clickListener);
			lassoPolyline.setMap(null);
			if (firstPointMarker) firstPointMarker.setMap(null);
			map.setOptions({ draggable: true });
			lassoPathRef.current = [];
		};
	}, [map, lassoMode]);

	// Loading state
	if (vm.isLoading) {
		return (
			<Box
				data-testid="issue-map-container"
				data-mode={visualizationMode}
				className="issue-map-container loading"
				style={{ height }}
			>
				Loading map...
			</Box>
		);
	}

	return (
		<Box
			data-testid="issue-map-container"
			data-mode={visualizationMode}
			className={`issue-map-container${lassoMode ? " lasso-mode" : ""}`}
			style={{ height }}
		>
			<Map
				mapId="wizardIssueMap"
				defaultCenter={vm.center}
				defaultZoom={14}
				style={{ width: "100%", height: "100%" }}
				gestureHandling="cooperative"
			>
				<MapBoundsFitter coordinates={vm.allCoordinates} />
				{vm.polygonElements}
				{vm.markerElements}
			</Map>
		</Box>
	);
}
